import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getMonthCalendarGrid, getMondayOf, costTier } from '../utils/monthGrid';
import { formatCurrency } from '../utils/format';
import { formatDuration } from '../utils/formatTime';

const DAY_HEADERS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

const TIER_CLASSES = [
  'bg-gray-50 text-gray-500',
  'bg-green-100 text-gray-700',
  'bg-green-200 text-gray-800',
  'bg-green-400 text-white',
  'bg-green-600 text-white',
];

const CURRENCY_SYMBOL = { INR: '₹', USD: '$', EUR: '€', GBP: '£' };

function compactCost(costSmallestUnit, currency) {
  const amount = costSmallestUnit / 100;
  if (amount >= 1000) {
    const sym = CURRENCY_SYMBOL[currency] ?? currency;
    return `${sym}${(amount / 1000).toFixed(1)}k`;
  }
  return formatCurrency(amount);
}

function toYMD(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function MonthOverview({ weekStart, monthCosts, monthLoading, currency, onDayClick }) {
  const { t } = useTranslation('scheduler');

  const year = weekStart.getFullYear();
  const month = weekStart.getMonth();

  const grid = useMemo(() => getMonthCalendarGrid(year, month), [year, month]);

  // Max cost across in-month days only (excludes padding days)
  const monthMax = useMemo(() => {
    if (!monthCosts) return 0;
    let max = 0;
    for (const week of grid.weeks) {
      for (const dayYMD of week) {
        if (!dayYMD) continue;
        const data = monthCosts.get(dayYMD);
        if (data && data.costSmallestUnit > max) max = data.costSmallestUnit;
      }
    }
    return max;
  }, [monthCosts, grid]);

  // Running total: only in-month days
  const runningTotal = useMemo(() => {
    if (!monthCosts) return { minutes: 0, costSmallestUnit: 0 };
    let minutes = 0;
    let costSmallestUnit = 0;
    for (const week of grid.weeks) {
      for (const dayYMD of week) {
        if (!dayYMD) continue;
        const data = monthCosts.get(dayYMD);
        if (data) {
          minutes += data.minutes;
          costSmallestUnit += data.costSmallestUnit;
        }
      }
    }
    return { minutes, costSmallestUnit };
  }, [monthCosts, grid]);

  const today = toYMD(new Date());

  // Days belonging to the currently-displayed week
  const currentWeekDays = useMemo(() => {
    const mon = getMondayOf(weekStart);
    const days = new Set();
    for (let i = 0; i < 7; i++) {
      const d = new Date(mon);
      d.setDate(d.getDate() + i);
      days.add(toYMD(d));
    }
    return days;
  }, [weekStart]);

  const monthName = weekStart.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

  return (
    <div>
      <p className="text-sm font-semibold text-gray-700 mb-2">
        {t('monthOverview.title')} — {monthName}
      </p>

      {/* Day-of-week header */}
      <div className="flex gap-1 mb-1">
        {DAY_HEADERS.map((d) => (
          <div key={d} className="w-9 text-center text-xs text-gray-400 font-medium">
            {d}
          </div>
        ))}
      </div>

      {/* Week rows */}
      <div className="flex flex-col gap-1">
        {grid.weeks.map((week, wi) => (
          <div key={wi} className="flex gap-1">
            {week.map((dayYMD, di) => {
              // Loading skeleton
              if (monthLoading) {
                return (
                  <div
                    key={di}
                    className="w-9 h-9 rounded-md bg-gray-100 animate-pulse flex-shrink-0"
                  />
                );
              }

              // Padding cell (outside current month)
              if (!dayYMD) {
                return (
                  <div
                    key={di}
                    className="w-9 h-9 rounded-md bg-gray-50 flex-shrink-0"
                  />
                );
              }

              const data = monthCosts?.get(dayYMD);
              const tier = data ? costTier(data.costSmallestUnit, monthMax) : 0;
              const isToday = dayYMD === today;
              const isCurrentWeek = currentWeekDays.has(dayYMD);
              const dayNum = Number(dayYMD.split('-')[2]);

              const hoursLabel = data?.minutes ? formatDuration(data.minutes) : t('monthOverview.noData');
              const costLabel = data?.costSmallestUnit > 0
                ? formatCurrency(data.costSmallestUnit / 100)
                : '—';
              const tooltipDate = new Date(dayYMD + 'T12:00:00').toLocaleDateString('en-GB', {
                weekday: 'short', day: 'numeric', month: 'short',
              });

              const ringClass = isToday
                ? 'ring-2 ring-blue-500'
                : isCurrentWeek
                ? 'ring-1 ring-orange-300'
                : '';

              return (
                <button
                  key={di}
                  type="button"
                  title={t('monthOverview.dayCellTooltip', { date: tooltipDate, hours: hoursLabel, cost: costLabel })}
                  onClick={() => onDayClick(getMondayOf(new Date(dayYMD + 'T12:00:00')))}
                  className={`w-9 h-9 rounded-md flex flex-col items-center justify-center flex-shrink-0 transition-opacity hover:opacity-75 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-1 ${TIER_CLASSES[tier]} ${ringClass}`}
                >
                  <span className="text-xs font-semibold leading-tight">{dayNum}</span>
                  {tier >= 1 && data && (
                    <span className="text-[9px] leading-none opacity-90">
                      {compactCost(data.costSmallestUnit, currency)}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {/* Running month total */}
      {!monthLoading && monthCosts && (
        <p className="mt-2 text-sm text-gray-600">
          {t('monthOverview.runningTotal', {
            monthName: weekStart.toLocaleDateString('en-GB', { month: 'long' }),
            hours: formatDuration(runningTotal.minutes),
            cost: formatCurrency(runningTotal.costSmallestUnit / 100),
          })}
        </p>
      )}
    </div>
  );
}

export default MonthOverview;
